// Small display helpers shared by SSR and the browser, ported from the prototype (store.jsx `inr`,
// PublicProfile.jsx `ago`, Home.jsx `Avatar`). Deterministic so server and client render the same.

export function inr(n: number): string {
  return `₹${Math.round(n).toLocaleString('en-IN')}`;
}

// Rupees with paise when needed (bids can be ₹2.50).
export function inrExact(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded)
    ? inr(rounded)
    : `₹${rounded.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

// Pay-per-click figures (ADR-004). CTR as a percentage with one decimal ("4.2"), 0 when nothing was
// shown; average cost per click over the clicks that were actually charged.
export function ctrPercent(clicks: number, shown: number): number {
  return shown > 0 ? Math.round((clicks / shown) * 1000) / 10 : 0;
}

export function avgCostPerClick(spend: number, chargedClicks: number): number {
  return chargedClicks > 0 ? Math.round((spend / chargedClicks) * 100) / 100 : 0;
}

const DAY_MS = 24 * 60 * 60 * 1000;

export function timeAgo(date: Date, now: Date = new Date()): string {
  const d = Math.floor((now.getTime() - date.getTime()) / DAY_MS);
  if (d < 1) return 'today';
  if (d < 30) return `${String(d)} day${d === 1 ? '' : 's'} ago`;
  const m = Math.floor(d / 30);
  if (m < 12) return `${String(m)} month${m === 1 ? '' : 's'} ago`;
  const y = Math.floor(d / 365);
  return `${String(y)} year${y === 1 ? '' : 's'} ago`;
}

// "26 September 2026" — fixed locale and time zone so SSR and hydration agree.
export function longDate(date: Date): string {
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: 'Asia/Kolkata',
  });
}

export function shortDate(date: Date): string {
  return date.toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    timeZone: 'Asia/Kolkata',
  });
}

export function initials(name: string): string {
  return name
    .replace(/^(Pandit|Pt\.|Dr\.|Acharya)\s+/i, '')
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w.charAt(0))
    .slice(0, 2)
    .join('')
    .toUpperCase();
}

export function avatarHue(name: string): number {
  return name.split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 360;
}

export function whatsappUrl(number: string | null | undefined): string | null {
  const digits = (number ?? '').replace(/\D/g, '');
  return digits ? `https://wa.me/${digits}` : null;
}

export function telUrl(number: string | null | undefined): string | null {
  const cleaned = (number ?? '').replace(/[^\d+]/g, '');
  return cleaned ? `tel:${cleaned}` : null;
}

// Only http(s) links are ever rendered as hrefs — a stored "javascript:" URL can't become clickable.
export function safeHttpUrl(url: string | null | undefined): string | null {
  if (!url) return null;
  try {
    const u = new URL(url);
    return u.protocol === 'https:' || u.protocol === 'http:' ? u.toString() : null;
  } catch {
    return null;
  }
}
